import React, { FC, useEffect } from "react";

import { useTranslation } from "react-i18next";
import { useMount } from "react-use";

import { PaginatedList } from "@/components/templates";
import { Contract, FeaturePageState, Permissions } from "@/shared/domain";
import { useAuth, useGlobalFilter, useNavigation, useRouterHook } from "@/shared/hooks";
import * as tagManager from "@/tagManager";
import { useUserFeedback } from "@/userFeedback";

import ContractItem from "../components/ContractItem/ContractItem";
import ContractTable from "../components/ContractTable/ContractTable";
import useContractQueries from "../useContractQueries";

const ALL_CONTRACTS_ID = "0";

export const compareNegativeAtEnd = (a: string, b: string): number => {
    const x = a !== "-1" ? +a : Infinity;
    const y = b !== "-1" ? +b : Infinity;
    return x - y;
};

const ContractPage: FC = () => {
    const {
        state: { filterId },
    } = useRouterHook<FeaturePageState>();
    const { user } = useAuth();
    const { t } = useTranslation();
    const { policyholdersGF } = useGlobalFilter();
    const { incUsageCount } = useUserFeedback();
    const { navToFeatureDocuments, navToContractsSearch } = useNavigation();

    const { tableMode, policyholders, useContractsPageQuery, useContractsCategoriesQuery } = useContractQueries();
    const {
        page,
        setPage,
        category,
        isFetching,
        setCategory,
        totalOfPages,
        result: contracts,
        handlePageSizeChange,
        orderBy,
        handleSort,
    } = useContractsPageQuery({ defaultCategory: filterId || ALL_CONTRACTS_ID });

    const { contractCategories, isLoadingCategories } = useContractsCategoriesQuery({ enabled: true });

    const totalNumberOfContracts = contractCategories.reduce((acc, item) => acc + item.totalItems, 0);

    const isLoading = isFetching || isLoadingCategories;

    useEffect(() => {
        if (!isLoading) {
            const validFilter =
                !!contractCategories.length &&
                contractCategories.some((contractCategory) => contractCategory.category.id === category);
            if (!validFilter && category !== ALL_CONTRACTS_ID) setCategory(ALL_CONTRACTS_ID);
        }
    }, [policyholders, isLoading]);

    useMount(() => {
        incUsageCount();
        tagManager.pushViewItemListEvent("contract", "Contracts");
    });

    useEffect(() => {
        if (contractCategories.length === 1 && contractCategories[0].category.id !== category) {
            setCategory(contractCategories[0].category.id);
            setPage(1);
        }
    }, [contractCategories]);

    const availableFilters = contractCategories
        .map(({ category: x, totalItems }) => ({
            id: x.id,
            label: x.name,
            quantity: totalItems,
        }))
        .sortBy("id", compareNegativeAtEnd); // put 'others' at the end

    if (availableFilters.length > 1) {
        availableFilters.unshift({
            id: ALL_CONTRACTS_ID,
            label: t("Common.All"),
            quantity: totalNumberOfContracts,
        });
    }
    const renderItem = (contract) => <ContractItem key={contract.id} contract={contract} />;
    const renderTable = (items: Contract[]) => (
        <ContractTable contracts={items} orderBy={orderBy} handleSort={handleSort} />
    );

    return (
        <PaginatedList<Contract>
            page={page}
            tableMode={tableMode}
            emptyListMessage={t("Contracts.Empty")}
            pageTitle={t("Common.Contracts")}
            isLoading={isLoadingCategories || isFetching}
            renderItem={renderItem}
            renderTable={renderTable}
            isFetchingList={isFetching}
            setPage={setPage}
            category={category}
            totalOfPages={totalOfPages}
            availableFilters={availableFilters}
            setCategory={setCategory}
            items={contracts}
            handlePageSizeChange={handlePageSizeChange}
            openSearch={navToContractsSearch}
            openLinkedDocuments={() => navToFeatureDocuments("contracts")}
            displayLinkedDocumentsButton={user.someHasPermission(Permissions.ViewLinkedDocuments, policyholdersGF)}
        />
    );
};

export default ContractPage;
